import { useMemo, useState, useEffect, useRef } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, Scale, Cylinder, Percent, Printer, Save, History, Trash2, Gauge } from "lucide-react";
import abvData from "@/abv_data.json";

const DEFAULT_TARE_KG = abvData.tare_kg;
const TARE_PRESETS = [54, 60, 120];
const ABV_PRESETS = [40, 40.3, 40.6, 43.5, 65, 75, 95];
const ALL = abvData.all_abv_entries;
const DENSITY_ENTRIES = abvData.density_entries || [];
const RECENTS_KEY = "dixon.alcohol.recents.v1";
const MAX_RECENTS = 10;

// Build lookup: "40.0" -> { factor, interpolated }
const LOOKUP = Object.fromEntries(
  ALL.map((e) => [e.abv.toFixed(1), { factor: e.factor, interpolated: !!e.interpolated }])
);
// Sorted numeric list for nearest-neighbour lookups
const SORTED_ABVS = ALL.map((e) => e.abv).sort((a, b) => a - b);

// Density lookup keyed on 1-decimal density string
const DENSITY_LOOKUP = Object.fromEntries(
  DENSITY_ENTRIES.map((d) => [d.density.toFixed(1), { abv: d.abv, factor: d.factor }])
);
const SORTED_DENSITIES = DENSITY_ENTRIES.map((d) => d.density).sort((a, b) => a - b);

const nearestAvailable = (value, list) => {
  if (!Number.isFinite(value)) return { below: null, above: null };
  let below = null, above = null;
  for (const a of list) {
    if (a < value) below = a;
    else if (a > value) { above = a; break; }
  }
  return { below, above };
};

// Tolerant parser: cleans comma decimal separators, stray spaces, % signs,
// multiple dots, trailing dot/comma. Returns { number, cleaned } or null.
const tolerantParse = (raw) => {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (s === "") return null;
  // Strip percent sign, kg/g unit suffixes, and all whitespace
  s = s.replace(/[%\s]/g, "").replace(/kg$|g$/i, "");
  // Comma decimal → dot
  s = s.replace(/,/g, ".");
  // Collapse multiple dots: keep only the last one (user may have typed trailing `.` after a decimal)
  const parts = s.split(".");
  if (parts.length > 2) {
    s = parts.slice(0, -1).join("") + "." + parts[parts.length - 1];
  }
  // Strip trailing dot
  s = s.replace(/\.$/, "");
  // Strip leading plus sign
  s = s.replace(/^\+/, "");
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const n = parseFloat(s);
  if (!Number.isFinite(n)) return null;
  return { number: n, cleaned: s };
};

const fmt = (n, d = 2) =>
  Number.isFinite(n) ? n.toFixed(d) : "—";

export default function Calculator() {
  const [weightStr, setWeightStr] = useState("");
  const [tareStr, setTareStr] = useState(String(DEFAULT_TARE_KG));
  const [abvStr, setAbvStr] = useState("");
  const [densityStr, setDensityStr] = useState("");
  const [abvMode, setAbvMode] = useState("abv"); // "abv" | "density"
  const [recents, setRecents] = useState([]);
  const [recentsOpen, setRecentsOpen] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const saveTimerRef = useRef(null);

  // Refs for keyboard flow
  const weightRef = useRef(null);
  const tareRef = useRef(null);
  const abvRef = useRef(null);
  const densityRef = useRef(null);
  const saveBtnRef = useRef(null);

  // Load recents on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENTS_KEY);
      if (raw) setRecents(JSON.parse(raw));
    } catch (e) {
      // ignore
    }
  }, []);

  const persistRecents = (next) => {
    setRecents(next);
    try {
      localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
    } catch (e) {
      // ignore storage errors (quota, private mode)
    }
  };

  // ABV source — in abv mode this comes from the typed ABV; in density mode it comes from the density lookup
  const abvParsed = useMemo(() => {
    if (abvMode === "density") {
      if (densityStr.trim() === "") return { kind: "empty" };
      const parsed = tolerantParse(densityStr);
      if (!parsed) {
        return { kind: "error", message: "Enter a hydrometer reading (e.g. 948.0).", nearest: { below: null, above: null } };
      }
      const v = parsed.number;
      if (v < SORTED_DENSITIES[0] || v > SORTED_DENSITIES[SORTED_DENSITIES.length - 1]) {
        return {
          kind: "error",
          message: `Hydrometer must be between ${SORTED_DENSITIES[0]} and ${SORTED_DENSITIES[SORTED_DENSITIES.length - 1]} kg/m³.`,
          nearest: nearestAvailable(v, SORTED_DENSITIES),
          nearestKind: "density",
        };
      }
      const key = v.toFixed(1);
      if (!(key in DENSITY_LOOKUP)) {
        return {
          kind: "error",
          message: `${key} kg/m³ is not on the hydrometer sheet (table uses 0.2 kg/m³ steps).`,
          nearest: nearestAvailable(v, SORTED_DENSITIES),
          nearestKind: "density",
        };
      }
      const d = DENSITY_LOOKUP[key];
      return { kind: "ok", abv: d.abv, factor: d.factor, interpolated: false, density: parseFloat(key) };
    }
    if (abvStr.trim() === "") return { kind: "empty" };
    const parsed = tolerantParse(abvStr);
    if (!parsed) {
      return { kind: "error", message: "Enter a number (e.g. 40 or 40.5).", nearest: { below: null, above: null }, nearestKind: "abv" };
    }
    const v = parsed.number;
    if (v < 1 || v > 100) {
      return { kind: "error", message: "ABV must be between 1% and 100%.", nearest: nearestAvailable(v, SORTED_ABVS), nearestKind: "abv" };
    }
    const key = v.toFixed(1);
    if (!(key in LOOKUP)) {
      return {
        kind: "error",
        message: `${key}% is not in the hydrometer table.`,
        nearest: nearestAvailable(v, SORTED_ABVS),
        nearestKind: "abv",
      };
    }
    const entry = LOOKUP[key];
    return { kind: "ok", abv: parseFloat(key), factor: entry.factor, interpolated: entry.interpolated };
  }, [abvStr, densityStr, abvMode]);

  const tareParsed = useMemo(() => {
    if (tareStr.trim() === "") {
      return { kind: "error", message: "Tare weight is required." };
    }
    const parsed = tolerantParse(tareStr);
    if (!parsed) {
      return { kind: "error", message: "Please enter a valid tare (e.g. 54 or 54.5)." };
    }
    if (parsed.number < 0) {
      return { kind: "error", message: "Tare must be zero or greater." };
    }
    return { kind: "ok", tare: parsed.number };
  }, [tareStr]);

  const parsed = useMemo(() => {
    if (weightStr.trim() === "") {
      return { kind: "empty" };
    }
    const p = tolerantParse(weightStr);
    if (!p) {
      return { kind: "error", message: "Please enter a valid number (e.g. 454 or 827.7)." };
    }
    const w = p.number;
    if (tareParsed.kind !== "ok") {
      return { kind: "error", message: "Fix the tare weight first." };
    }
    if (w < tareParsed.tare) {
      return {
        kind: "error",
        message: `Gross weight must be at least ${tareParsed.tare} kg (the tote tare). Lower values would mean a negative liquid weight.`,
      };
    }
    return { kind: "ok", gross: w, net: w - tareParsed.tare };
  }, [weightStr, tareParsed]);

  const abvEntry = abvParsed.kind === "ok" ? abvParsed : null;

  const result = useMemo(() => {
    if (parsed.kind !== "ok" || !abvEntry) return null;
    const litres = parsed.net * abvEntry.factor;
    const litresAbsolute = litres * (abvEntry.abv / 100);
    return { litres, litresAbsolute };
  }, [parsed, abvEntry]);

  const canSave = parsed.kind === "ok" && abvEntry != null && result != null && tareParsed.kind === "ok";

  const saveCurrent = () => {
    if (!canSave) return;
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      ts: new Date().toISOString(),
      gross: parsed.gross,
      tare: tareParsed.tare,
      net: parsed.net,
      abv: abvEntry.abv,
      factor: abvEntry.factor,
      interpolated: !!abvEntry.interpolated,
      litres: result.litres,
      litresAbsolute: result.litresAbsolute,
    };
    const next = [entry, ...recents.filter((r) => r.id !== entry.id)].slice(0, MAX_RECENTS);
    persistRecents(next);
    setJustSaved(true);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => setJustSaved(false), 1600);
  };

  const restoreRecent = (r) => {
    setWeightStr(String(r.gross));
    setTareStr(String(r.tare));
    setAbvMode("abv");
    setAbvStr(r.abv.toFixed(1));
    setRecentsOpen(false);
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const deleteRecent = (id) => {
    persistRecents(recents.filter((r) => r.id !== id));
  };

  const clearRecents = () => persistRecents([]);

  const printTicket = () => {
    if (typeof window !== "undefined") window.print();
  };

  // Keyboard flow: Weight → Tare → (ABV|Density) → Save
  const focusNext = (ref) => {
    if (ref?.current) {
      ref.current.focus();
      try { ref.current.select?.(); } catch (e) { /* ignore */ }
    }
  };
  const onEnter = (handler) => (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handler();
    }
  };

  // (Previously built dropdown groupings; replaced by typeable input + datalist.)

  return (
    <div className="paper-grain min-h-screen w-full">
      <div className="relative z-10 mx-auto max-w-2xl px-5 py-10 sm:px-8 sm:py-14">
        {/* Header */}
        <header className="mb-10" data-testid="app-header">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full border border-[color:var(--rule-strong)] bg-[color:var(--paper-2)] grid place-items-center">
              <Cylinder className="h-5 w-5" style={{ color: "var(--copper)" }} />
            </div>
            <div>
              <p
                className="font-mono text-base sm:text-lg uppercase tracking-[0.22em]"
                style={{ color: "var(--copper-deep)" }}
              >
                BD&apos;s
              </p>
              <h1
                className="font-serif text-3xl sm:text-4xl font-semibold leading-tight"
                style={{ color: "var(--ink)" }}
                data-testid="app-title"
              >
                Alcohol Liters Calculator
              </h1>
            </div>
          </div>
          <p className="mt-3 max-w-prose text-sm" style={{ color: "var(--ink-soft)" }}>
            Enter the scale&apos;s gross tote weight, type the ABV from the hydrometer table, and read the exact litres of liquid.
          </p>
          <div className="copper-rule mt-6" />
        </header>

        {/* Inputs */}
        <section className="grid gap-6 sm:grid-cols-2" data-testid="input-section">
          <div>
            <Label htmlFor="weight" className="font-mono text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
              Tote Weight (kg)
            </Label>
            <div className="relative mt-2">
              <Scale className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--ink-soft)" }} />
              <Input
                id="weight"
                data-testid="weight-input"
                inputMode="decimal"
                placeholder="e.g. 454.0 kg or 454.5 kg"
                value={weightStr}
                onChange={(e) => setWeightStr(e.target.value)}
                ref={weightRef}
                onKeyDown={onEnter(() => focusNext(tareRef))}
                className="pl-9 h-12 text-lg font-mono placeholder:text-sm bg-[color:var(--paper-2)] border-[color:var(--rule-strong)] focus-visible:ring-[color:var(--copper)]"
              />
            </div>
            <p className="mt-2 text-xs" style={{ color: "var(--ink-soft)" }}>
              Gross weight on the scale, including the tote.
            </p>
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <Label htmlFor="tare" className="font-mono text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
                Tote Tare (kg)
              </Label>
              {tareStr !== String(DEFAULT_TARE_KG) && (
                <button
                  type="button"
                  data-testid="tare-reset-btn"
                  onClick={() => setTareStr(String(DEFAULT_TARE_KG))}
                  className="font-mono text-[11px] uppercase tracking-widest underline-offset-4 hover:underline"
                  style={{ color: "var(--copper-deep)" }}
                >
                  Reset to {DEFAULT_TARE_KG}
                </button>
              )}
            </div>
            <div className="relative mt-2">
              <Scale className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--ink-soft)" }} />
              <Input
                id="tare"
                data-testid="tare-input"
                inputMode="decimal"
                placeholder="e.g. 54"
                value={tareStr}
                onChange={(e) => setTareStr(e.target.value)}
                ref={tareRef}
                onKeyDown={onEnter(() => focusNext(abvMode === "density" ? densityRef : abvRef))}
                className="pl-9 h-12 text-lg font-mono placeholder:text-sm bg-[color:var(--paper-2)] border-[color:var(--rule-strong)] focus-visible:ring-[color:var(--copper)]"
              />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="tare-presets">
              <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
                presets
              </span>
              {TARE_PRESETS.map((p) => {
                const active = parseFloat(tareStr) === p;
                return (
                  <button
                    key={p}
                    type="button"
                    data-testid={`tare-preset-${p}`}
                    onClick={() => setTareStr(String(p))}
                    className={`font-mono text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      active
                        ? "border-[color:var(--copper)] bg-[color:var(--copper)] text-[color:var(--paper)]"
                        : "border-[color:var(--rule-strong)] bg-[color:var(--paper)] hover:bg-[color:var(--paper-2)]"
                    }`}
                    style={{ color: active ? "var(--paper)" : "var(--ink)" }}
                  >
                    {p} kg
                  </button>
                );
              })}
            </div>
          </div>

          <div className="sm:col-span-2">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <Label htmlFor={abvMode === "density" ? "density" : "abv"} className="font-mono text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
                  {abvMode === "density" ? "Hydrometer Reading (kg/m³)" : "Alcohol % (ABV)"}
                </Label>
                <div className="inline-flex rounded-full border border-[color:var(--rule-strong)] p-0.5 bg-[color:var(--paper)]" data-testid="abv-mode-toggle">
                  <button
                    type="button"
                    data-testid="abv-mode-abv"
                    onClick={() => setAbvMode("abv")}
                    className={`font-mono text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full transition-colors ${
                      abvMode === "abv"
                        ? "bg-[color:var(--copper)] text-[color:var(--paper)]"
                        : "text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]"
                    }`}
                  >
                    ABV
                  </button>
                  <button
                    type="button"
                    data-testid="abv-mode-density"
                    onClick={() => setAbvMode("density")}
                    className={`font-mono text-[11px] uppercase tracking-widest px-2.5 py-1 rounded-full transition-colors ${
                      abvMode === "density"
                        ? "bg-[color:var(--copper)] text-[color:var(--paper)]"
                        : "text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]"
                    }`}
                  >
                    Density
                  </button>
                </div>
              </div>
              {abvEntry && (
                <span
                  data-testid="abv-factor-hint"
                  className="font-mono text-[11px] uppercase tracking-widest flex items-center gap-2"
                  style={{ color: "var(--copper-deep)" }}
                >
                  {abvMode === "density" ? `${abvEntry.abv}% · ` : ""}factor {abvEntry.factor.toFixed(4)}
                  {abvEntry.interpolated && (
                    <span
                      data-testid="abv-interpolated-badge"
                      className="px-1.5 py-0.5 rounded-sm font-mono text-[9px] tracking-widest"
                      style={{ background: "var(--copper)", color: "var(--paper)" }}
                      title="This factor was linearly interpolated — the exact value is not in the hydrometer sheet"
                    >
                      interpolated
                    </span>
                  )}
                </span>
              )}
            </div>
            {abvMode === "abv" ? (
              <div className="relative mt-2">
                <Percent className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--ink-soft)" }} />
                <Input
                  id="abv"
                  data-testid="abv-input"
                  inputMode="decimal"
                  placeholder="e.g. 40% or 40.5%"
                  value={abvStr}
                  onChange={(e) => setAbvStr(e.target.value)}
                  ref={abvRef}
                  onKeyDown={onEnter(() => focusNext(saveBtnRef))}
                  list="abv-suggestions"
                  autoComplete="off"
                  aria-invalid={abvParsed.kind === "error"}
                  className={`pl-9 h-12 text-lg font-mono placeholder:text-sm bg-[color:var(--paper-2)] focus-visible:ring-[color:var(--copper)] ${
                    abvParsed.kind === "error"
                      ? "border-[color:var(--warn)] ring-2 ring-[color:var(--warn)]/30 bg-[color:var(--warn)]/5 text-[color:var(--warn)]"
                      : "border-[color:var(--rule-strong)]"
                  }`}
                />
                <datalist id="abv-suggestions">
                  {ALL.map((e) => (
                    <option key={e.abv} value={e.abv.toFixed(1)} />
                  ))}
                </datalist>
              </div>
            ) : (
              <div className="relative mt-2">
                <Gauge className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--ink-soft)" }} />
                <Input
                  id="density"
                  data-testid="density-input"
                  inputMode="decimal"
                  placeholder="e.g. 948.0"
                  value={densityStr}
                  onChange={(e) => setDensityStr(e.target.value)}
                  ref={densityRef}
                  onKeyDown={onEnter(() => focusNext(saveBtnRef))}
                  list="density-suggestions"
                  autoComplete="off"
                  aria-invalid={abvParsed.kind === "error"}
                  className={`pl-9 h-12 text-lg font-mono placeholder:text-sm bg-[color:var(--paper-2)] focus-visible:ring-[color:var(--copper)] ${
                    abvParsed.kind === "error"
                      ? "border-[color:var(--warn)] ring-2 ring-[color:var(--warn)]/30 bg-[color:var(--warn)]/5 text-[color:var(--warn)]"
                      : "border-[color:var(--rule-strong)]"
                  }`}
                />
                <datalist id="density-suggestions">
                  {DENSITY_ENTRIES.map((d) => (
                    <option key={d.density} value={d.density.toFixed(1)} />
                  ))}
                </datalist>
              </div>
            )}
            {abvMode === "abv" && (
              <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="abv-presets">
                <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
                  presets
                </span>
                {ABV_PRESETS.map((p) => {
                  const key = p.toFixed(1);
                  const active = abvStr.trim() !== "" && (() => {
                    const parsed = tolerantParse(abvStr);
                    return !!parsed && parsed.number.toFixed(1) === key;
                  })();
                  return (
                    <button
                      key={key}
                      type="button"
                      data-testid={`abv-preset-${key}`}
                      onClick={() => setAbvStr(key)}
                      className={`font-mono text-xs px-2.5 py-1 rounded-full border transition-colors ${
                        active
                          ? "border-[color:var(--copper)] bg-[color:var(--copper)] text-[color:var(--paper)]"
                          : "border-[color:var(--rule-strong)] bg-[color:var(--paper)] hover:bg-[color:var(--paper-2)]"
                      }`}
                      style={{ color: active ? "var(--paper)" : "var(--ink)" }}
                    >
                      {p % 1 === 0 ? `${p}%` : `${p}%`}
                    </button>
                  );
                })}
              </div>
            )}
            <p className="mt-2 text-xs" style={{ color: abvParsed.kind === "error" ? "var(--warn)" : "var(--ink-soft)" }}>
              {abvParsed.kind === "error"
                ? abvParsed.message
                : abvMode === "density"
                ? "Type the hydrometer reading at 20 °C in 0.2 kg/m³ steps. ABV and factor come straight from the sheet."
                : "Type any value from the hydrometer table (0.1% steps). Interpolated whole numbers are tagged."}
            </p>
            {abvParsed.kind === "error" && (abvParsed.nearest?.below != null || abvParsed.nearest?.above != null) && (
              <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="abv-nearest-chips">
                <span className="font-mono text-[10px] uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
                  nearest
                </span>
                {abvParsed.nearest.below != null && (
                  <button
                    type="button"
                    data-testid="abv-nearest-below"
                    onClick={() => (abvMode === "density"
                      ? setDensityStr(abvParsed.nearest.below.toFixed(1))
                      : setAbvStr(abvParsed.nearest.below.toFixed(1)))}
                    className="font-mono text-xs px-2.5 py-1 rounded-full border border-[color:var(--rule-strong)] bg-[color:var(--paper)] hover:bg-[color:var(--paper-2)]"
                    style={{ color: "var(--ink)" }}
                  >
                    ↓ {abvParsed.nearest.below.toFixed(1)}{abvMode === "density" ? " kg/m³" : "%"}
                  </button>
                )}
                {abvParsed.nearest.above != null && (
                  <button
                    type="button"
                    data-testid="abv-nearest-above"
                    onClick={() => (abvMode === "density"
                      ? setDensityStr(abvParsed.nearest.above.toFixed(1))
                      : setAbvStr(abvParsed.nearest.above.toFixed(1)))}
                    className="font-mono text-xs px-2.5 py-1 rounded-full border border-[color:var(--rule-strong)] bg-[color:var(--paper)] hover:bg-[color:var(--paper-2)]"
                    style={{ color: "var(--ink)" }}
                  >
                    ↑ {abvParsed.nearest.above.toFixed(1)}{abvMode === "density" ? " kg/m³" : "%"}
                  </button>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Validation */}
        {tareParsed.kind === "error" && (
          <Alert
            variant="destructive"
            className="mt-6 border-[color:var(--warn)]/40 bg-[color:var(--paper-2)]"
            data-testid="tare-error"
          >
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle className="font-serif">Invalid tare</AlertTitle>
            <AlertDescription>{tareParsed.message}</AlertDescription>
          </Alert>
        )}
        {parsed.kind === "error" && (
          <Alert
            variant="destructive"
            className="mt-6 border-[color:var(--warn)]/40 bg-[color:var(--paper-2)]"
            data-testid="weight-error"
          >
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle className="font-serif">Invalid weight</AlertTitle>
            <AlertDescription>{parsed.message}</AlertDescription>
          </Alert>
        )}

        {/* Primary results */}
        <section className="mt-10 grid gap-6 sm:grid-cols-3" data-testid="results-section">
          <ResultBlock
            label="Net Liquid Weight"
            value={parsed.kind === "ok" ? `${fmt(parsed.net)} kg` : "—"}
            testId="net-weight-display"
          />
          <ResultBlock
            label="Total Litres"
            value={result ? `${fmt(result.litres)} L` : "—"}
            emphasised
            testId="total-litres-display"
          />
          <ResultBlock
            label="LAA"
            sublabel="Litres of Absolute Alcohol"
            value={result ? `${fmt(result.litresAbsolute)} L` : "—"}
            emphasised
            testId="absolute-alcohol-display"
          />
        </section>

        {/* Calculation details */}
        <section
          className="mt-10 rounded-md border border-[color:var(--rule-strong)] bg-[color:var(--paper-2)] print-ticket"
          data-testid="calculation-details"
        >
          <div className="flex items-baseline justify-between px-5 py-3 border-b border-[color:var(--rule)]">
            <h2 className="font-serif text-lg">Calculation Details</h2>
            <span className="font-mono text-[11px] uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
              step by step
            </span>
          </div>
          <dl className="divide-y divide-[color:var(--rule)]">
            <DetailRow label="Gross Tote Weight" value={parsed.kind === "ok" ? `${fmt(parsed.gross)} kg` : "—"} testId="detail-gross" />
            <DetailRow label="Tote Tare" value={tareParsed.kind === "ok" ? `${fmt(tareParsed.tare)} kg` : "—"} testId="detail-tare" />
            <DetailRow label="Net Liquid Weight" value={parsed.kind === "ok" ? `${fmt(parsed.net)} kg` : "—"} testId="detail-net" />
            <DetailRow label="ABV" value={abvEntry ? `${abvEntry.abv}%` : "—"} testId="detail-abv" />
            <DetailRow label="Conversion Factor" value={abvEntry ? `${abvEntry.factor.toFixed(4)}${abvEntry.interpolated ? " (interpolated)" : ""}` : "—"} testId="detail-factor" />
            <DetailRow
              label="Total Litres"
              value={result ? `${fmt(result.litres)} L` : "—"}
              testId="detail-litres"
              strong
            />
            <DetailRow
              label="LAA (Litres of Absolute Alcohol)"
              value={result ? `${fmt(result.litresAbsolute)} L` : "—"}
              testId="detail-absolute-alcohol"
              strong
            />
          </dl>
          <div className="print-only px-5 py-3 border-t border-[color:var(--rule)] font-mono text-[11px] uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
            BD&apos;s · Printed <span>{new Date().toLocaleString()}</span>
          </div>
        </section>

        {/* Actions */}
        <section className="mt-6 flex flex-wrap gap-3 no-print" data-testid="actions-section">
          <button
            type="button"
            data-testid="save-calc-btn"
            onClick={saveCurrent}
            disabled={!canSave}
            ref={saveBtnRef}
            className={`inline-flex items-center gap-2 px-4 h-11 rounded-md font-mono text-xs uppercase tracking-widest border transition-colors ${
              canSave
                ? "bg-[color:var(--copper)] text-[color:var(--paper)] border-[color:var(--copper)] hover:bg-[color:var(--copper-deep)]"
                : "bg-[color:var(--paper-2)] text-[color:var(--ink-soft)] border-[color:var(--rule-strong)] cursor-not-allowed"
            }`}
          >
            <Save className="h-4 w-4" />
            {justSaved ? "Saved" : "Save Calculation"}
          </button>
          <button
            type="button"
            data-testid="print-ticket-btn"
            onClick={printTicket}
            disabled={!canSave}
            className={`inline-flex items-center gap-2 px-4 h-11 rounded-md font-mono text-xs uppercase tracking-widest border transition-colors ${
              canSave
                ? "bg-[color:var(--paper)] text-[color:var(--ink)] border-[color:var(--rule-strong)] hover:bg-[color:var(--paper-2)]"
                : "bg-[color:var(--paper-2)] text-[color:var(--ink-soft)] border-[color:var(--rule-strong)] cursor-not-allowed"
            }`}
          >
            <Printer className="h-4 w-4" />
            Print Ticket
          </button>
          <button
            type="button"
            data-testid="toggle-recents-btn"
            onClick={() => setRecentsOpen((o) => !o)}
            className="inline-flex items-center gap-2 ml-auto px-4 h-11 rounded-md font-mono text-xs uppercase tracking-widest border border-[color:var(--rule-strong)] bg-[color:var(--paper)] text-[color:var(--ink)] hover:bg-[color:var(--paper-2)]"
          >
            <History className="h-4 w-4" />
            Recent{recents.length > 0 ? ` (${recents.length})` : ""}
          </button>
        </section>

        {/* Recents */}
        {recentsOpen && (
          <section
            className="mt-4 rounded-md border border-[color:var(--rule-strong)] bg-[color:var(--paper-2)] no-print"
            data-testid="recents-section"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-[color:var(--rule)]">
              <h2 className="font-serif text-lg">Recent Calculations</h2>
              {recents.length > 0 && (
                <button
                  type="button"
                  data-testid="clear-recents-btn"
                  onClick={clearRecents}
                  className="font-mono text-[11px] uppercase tracking-widest underline-offset-4 hover:underline"
                  style={{ color: "var(--copper-deep)" }}
                >
                  Clear all
                </button>
              )}
            </div>
            {recents.length === 0 ? (
              <p className="px-5 py-6 text-sm" style={{ color: "var(--ink-soft)" }}>
                No saved calculations yet. Tap <span className="font-mono">Save Calculation</span> above to keep one here.
              </p>
            ) : (
              <ul className="divide-y divide-[color:var(--rule)]">
                {recents.map((r) => (
                  <li
                    key={r.id}
                    className="flex items-center gap-3 px-5 py-3"
                    data-testid={`recent-item-${r.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => restoreRecent(r)}
                      className="flex-1 text-left group"
                      data-testid={`recent-restore-${r.id}`}
                    >
                      <div className="flex items-baseline gap-2 font-mono text-sm">
                        <span style={{ color: "var(--ink)" }}>{r.gross.toFixed(2)} kg</span>
                        <span style={{ color: "var(--ink-soft)" }}>@</span>
                        <span style={{ color: "var(--ink)" }}>{r.abv.toFixed(1)}%</span>
                        {r.interpolated && (
                          <span className="font-mono text-[9px] px-1.5 py-0.5 rounded-sm uppercase tracking-widest" style={{ background: "var(--copper)", color: "var(--paper)" }}>
                            interp
                          </span>
                        )}
                        <span className="ml-auto font-semibold" style={{ color: "var(--copper-deep)" }}>
                          {r.litres.toFixed(2)} L
                        </span>
                      </div>
                      <div className="mt-0.5 font-mono text-[11px]" style={{ color: "var(--ink-soft)" }}>
                        tare {r.tare.toFixed(2)} kg · net {r.net.toFixed(2)} kg · factor {r.factor.toFixed(4)} · LAA {Number.isFinite(r.litresAbsolute) ? r.litresAbsolute.toFixed(2) : "—"} L · {new Date(r.ts).toLocaleString()}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteRecent(r.id)}
                      aria-label="Delete entry"
                      data-testid={`recent-delete-${r.id}`}
                      className="p-2 rounded-md hover:bg-[color:var(--paper)] transition-colors"
                      style={{ color: "var(--ink-soft)" }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <footer className="mt-12 pb-6 text-center font-mono text-[11px] uppercase tracking-widest no-print" style={{ color: "var(--ink-soft)" }}>
          V1 · Hydrometer table is the single source of truth
        </footer>
      </div>
    </div>
  );
}

function ResultBlock({ label, sublabel, value, emphasised, testId }) {
  return (
    <div
      className={`rounded-md border ${
        emphasised ? "border-[color:var(--copper)]" : "border-[color:var(--rule-strong)]"
      } bg-[color:var(--paper-2)] px-5 py-4`}
      data-testid={testId}
    >
      <p
        className="font-mono text-[11px] uppercase tracking-widest"
        style={{ color: emphasised ? "var(--copper-deep)" : "var(--ink-soft)" }}
      >
        {label}
      </p>
      {sublabel && (
        <p
          className="font-mono text-[10px] tracking-wide mt-0.5"
          style={{ color: "var(--ink-soft)" }}
        >
          {sublabel}
        </p>
      )}
      <p
        className={`mt-1 font-serif ${
          emphasised ? "text-4xl sm:text-5xl" : "text-3xl sm:text-4xl"
        } font-semibold`}
        style={{ color: emphasised ? "var(--copper-deep)" : "var(--ink)" }}
      >
        {value}
      </p>
    </div>
  );
}

function DetailRow({ label, value, testId, strong }) {
  return (
    <div className="flex items-center justify-between px-5 py-3" data-testid={testId}>
      <dt className="text-sm" style={{ color: "var(--ink-soft)" }}>
        {label}
      </dt>
      <dd
        className={`font-mono ${strong ? "text-lg font-semibold" : "text-base"}`}
        style={{ color: strong ? "var(--copper-deep)" : "var(--ink)" }}
      >
        {value}
      </dd>
    </div>
  );
}
